// MF-67 — Colour helpers for tenant-picked colours (severities, categories…). Schools choose any colour,
// so we never paint it raw at full strength: badges render it as a soft tint + a text tone darkened
// until it reads at WCAG AA (4.5:1) on that tint. Keeps loud picks (gold, neon red) calm and legible.

// Calm, mid-saturation presets offered in the colour pickers.
export const PRESET_COLORS = [
  '#64748B', // slate
  '#2563EB', // blue
  '#4F46E5', // indigo
  '#7C3AED', // violet
  '#DB2777', // pink
  '#DC2626', // red
  '#EA580C', // orange
  '#B7791F', // bronze
  '#CA8A04', // gold
  '#65A30D', // lime
  '#16A34A', // green
  '#0D9488' // teal
]

// Fallback colours for the default type codes (used before a tenant's types load). They
// match the seeded defaults: the CVD-checked aqua / orange pair + a slate neutral.
export const TYPE_FALLBACK_COLORS = {
  positive: '#1BAF7A',
  negative: '#EB6834',
  neutral: '#64748B'
}

export const DEFAULT_COLOR = '#64748B'

export const isHex = (s) => /^#[0-9a-f]{6}$/i.test(String(s || '').trim())

const toRgb = (hex) => {
  const h = isHex(hex) ? hex.trim().slice(1) : DEFAULT_COLOR.slice(1)
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
}

const channel = (c) => {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
}
const luminance = ([r, g, b]) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}
const mix = (rgb, target, t) => rgb.map((c, i) => Math.round(c + (target[i] - c) * t))
const css = ([r, g, b], a = 1) => (a === 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`)

const TINT = 0.14
const WHITE = [255, 255, 255]
const BLACK = [0, 0, 0]

// Soft badge tokens: tinted background, subtle border, readable text, and the raw colour as a dot.
export const badgeTokens = (hex) => {
  const rgb = toRgb(hex)
  const bgOnWhite = mix(WHITE, rgb, TINT)
  let text = rgb
  for (let t = 0.1; contrast(text, bgOnWhite) < 4.5 && t <= 0.9; t += 0.1) text = mix(rgb, BLACK, t)
  return {
    bg: css(rgb, TINT),
    border: css(rgb, 0.35),
    text: css(text),
    dot: css(rgb)
  }
}

// Text colour for a SOLID fill of `hex` (white or near-black, whichever reads better).
export const readableText = (hex) => {
  const rgb = toRgb(hex)
  return contrast(WHITE, rgb) >= contrast([31, 41, 55], rgb) ? '#fff' : '#1f2937'
}

// Case status colours (open → actioned → resolved).
export const STATUS_COLORS = {
  open: '#2563EB',
  actioned: '#D97706',
  resolved: '#16A34A'
}
